import { useCurrentStateAndParams } from "@uirouter/react";

import { GAWidget } from "app/pages/activation/components/GAWidget/GAWidget";
import { ZERO } from "app/pages/activation/components/Topics/Topic/constants";
import { Topics } from "app/pages/activation/components/Topics/Topics";
import { Widget } from "app/pages/activation/components/Widget/Widget";
import { ActivationProjectContextProvider } from "app/pages/activation/context/Project/ProjectContext";
import { SubscriptionProvider } from "app/pages/activation/context/Subscription/SubscriptionProvider";
import useProjectData from "app/pages/activation/hooks/useProjectData";

import { AddonPopup } from "../../components/AddonPopup/AddonPopup";
import { Header } from "../../components/Header/Header";
import { CountryUpdateModal } from "../../components/Modals/CountryUpdateModal/CountryUpdateModal";
import { RankTrackerWidget } from "../../components/RankWidget/RankTrackerWidget";
import { TopicsContextProvider } from "../../context/Topics/TopicsContext";
import { TopicsDataContextProvider } from "../../context/Topics/TopicsDataContext";
import { generateAutomationName } from "../../helpers/generateAutomationName";

import * as SC from "./styles";

const Project = () => {
    const {
        params: { key: activationId, webSource, country, timeGranularity },
    } = useCurrentStateAndParams();

    const {
        activationObject,
        siteMeta,
        isGeneralDataLoading,
        isMetricLoaderActive,
        defaultDuration,
        formattedDuration,
        metricData,
        firstPartyDataSource,
        setFirstPartyDataSource,
        isViewerMode,
    } = useProjectData(activationId, country, timeGranularity);

    return (
        <SubscriptionProvider
            isViewerMode={isViewerMode}
            activationId={activationId}
            benchmarkId={activationObject?.assets?.benchmarkId}
        >
            <ActivationProjectContextProvider>
                <Header
                    activationId={activationId}
                    domain={activationObject?.primaryDomain}
                    favIconUrl={siteMeta ? siteMeta[activationObject?.primaryDomain]?.icon : ""}
                    isLoading={isGeneralDataLoading}
                    country={country}
                    webSource={webSource}
                    timeGranularity={timeGranularity}
                    isViewerMode={isViewerMode}
                    sharedBy={activationObject?.sharedBy}
                    benchmarkId={activationObject?.assets?.benchmarkId}
                    defaultDuration={defaultDuration}
                />

                <AddonPopup />

                <SC.MainContainer data-automation={generateAutomationName("home", ["container"])}>
                    {activationObject ? <CountryUpdateModal activationObject={activationObject} /> : null}

                    <GAWidget
                        isDataLoading={isGeneralDataLoading}
                        activationId={activationObject?.id}
                        granularity={timeGranularity}
                        duration={formattedDuration}
                        domain={activationObject?.primaryDomain}
                        benchmarkId={activationObject?.assets?.benchmarkId}
                        defaultDuration={defaultDuration}
                        source={firstPartyDataSource}
                        setSource={setFirstPartyDataSource}
                        isViewerMode={isViewerMode}
                    />

                    <Widget
                        primaryDomain={activationObject?.primaryDomain}
                        benchmarkId={activationObject?.assets?.benchmarkId}
                        isLoading={isMetricLoaderActive}
                        formattedDuration={formattedDuration}
                        defaultDuration={defaultDuration}
                        trafficShareData={metricData}
                    />
                    <TopicsDataContextProvider
                        duration={defaultDuration}
                        isTopicsCreated={!!activationObject?.assets?.topics?.length}
                        createdTopicsLength={activationObject?.assets?.topics?.length ?? ZERO}
                        isActivationLoading={isGeneralDataLoading}
                    >
                        <TopicsContextProvider>
                            <Topics
                                formattedDuration={formattedDuration}
                                createdTopicsLength={activationObject?.assets?.topics?.length ?? ZERO}
                                isViewerMode={isViewerMode}
                            />

                            <RankTrackerWidget isViewerMode={isViewerMode} />
                        </TopicsContextProvider>
                    </TopicsDataContextProvider>
                </SC.MainContainer>
            </ActivationProjectContextProvider>
        </SubscriptionProvider>
    );
};

Project.displayName = "Home";

export default Project;
